import { API_ENDPOINTS, getApiUrl } from "../config/api.config";

/**
 * Get current system settings
 * @returns {Promise<Object>} Settings object
 */
export const getSettings = async () => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.SETTINGS), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Send cookies with request
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to fetch settings");
    }

    return data.data;
  } catch (error) {
    console.error("Error fetching settings:", error);
    throw error;
  }
};

/**
 * Update system settings
 * @param {Object} settings - Settings to update
 * @returns {Promise<Object>} Updated settings
 */
export const updateSettings = async (settings) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.SETTINGS), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Send cookies with request
      body: JSON.stringify(settings),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to update settings");
    }

    return data.data;
  } catch (error) {
    console.error("Error updating settings:", error);
    throw error;
  }
};

/**
 * Toggle limit order setting
 * @param {boolean} enabled - Whether limit orders should be enabled
 * @param {number} percent - Percentage for limit orders (1-100)
 * @returns {Promise<Object>} Updated settings
 */
export const toggleLimitOrderSetting = async (enabled, percent = null) => {
  // Get current settings to maintain other values
  const currentSettings = await getSettings().catch(() => ({}));

  // Prepare the update payload with the structure matching the new API
  const settings = {
    // Only include properties we're changing
    limit_order: {
      enabled: enabled,
      value: enabled
        ? percent !== null
          ? percent
          : currentSettings.limit_order?.value || 25
        : 0,
    },
  };

  return updateSettings(settings);
};

/**
 * Update PNL settings
 * @param {string} pnlType - Type of PNL setting ('exit_pnl' or 'target_pnl')
 * @param {boolean} enabled - Whether the PNL feature is enabled
 * @param {number} value - The PNL value
 * @returns {Promise<Object>} Updated settings
 */
export const updatePnlSettings = async (pnlType, enabled, value) => {
  // Make sure we're using a valid PNL type
  if (pnlType !== "exit_pnl" && pnlType !== "target_pnl") {
    throw new Error(
      'Invalid PNL type. Must be either "exit_pnl" or "target_pnl"',
    );
  }

  const settings = {
    [pnlType]: {
      value: value,
      enabled: enabled,
    },
  };

  return updateSettings(settings);
};

/**
 * Update product type setting
 * @param {string} productType - Product type (MIS, NRML, etc.)
 * @returns {Promise<Object>} Updated settings
 */
export const updateProductType = async (productType) => {
  const settings = {
    order_product_type: productType,
  };

  return updateSettings(settings);
};

/**
 * Update trailing stop settings
 * @param {boolean} enabled - Whether trailing stop is enabled
 * @param {number} trailingPercent - Trailing percentage (5-55 in UI)
 * @param {number} activationProfit - Activation profit threshold
 * @returns {Promise<Object>} Updated settings
 */
export const updateTrailingStop = async (
  enabled,
  trailingPercent,
  activationProfit,
) => {
  const settings = {
    trailing_stop: {
      enabled: enabled,
      trailing_percent: trailingPercent,
      activation_profit: activationProfit,
    },
  };

  return updateSettings(settings);
};
